import { getLocalDateTimeString } from '@/utils/date-time';
import { CircleCircle } from '@calm/evexia-web-sdk';
import createRbcApi from '@rhg/libs/api/create-rbc-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryClient,
    queryKeys,
    useMutation,
    useQuery,
} from '@rhg/libs/react-query';
import { useActiveCircleId } from '../use-active-circle';
import { useCircle } from '../use-circle';

export const InitiallyLoadedRecommendationsStorageKey =
    'initiallyLoadedRecommendations';

export function setHasInitiallyLoadedRecommendations() {
    sessionStorage.setItem(InitiallyLoadedRecommendationsStorageKey, 'true');
}
export function getHasInitiallyLoadedRecommendations() {
    return sessionStorage.getItem(InitiallyLoadedRecommendationsStorageKey);
}

export const circleRecommendationsQueryOptions = {
    queryKey: (circleId: number) => [queryKeys.recommendations, circleId],
    queryFn: async ({
        getAccessTokenSilently,
        circle,
        forceRefresh,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        circle?: CircleCircle;
        forceRefresh?: boolean;
    }) => {
        if (!circle) return {};
        const rbcApi = createRbcApi(getAccessTokenSilently);
        const circleAuthorization = circle?.authorizationJWT ?? '';
        const localDateTime = getLocalDateTimeString();

        const response = await rbcApi.getV1RecommendationsCircle({
            circleAuthorizationToken: circleAuthorization,
            recipientId: circle.recipient.id,
            localDateTime,
            forceRefresh,
        });
        return response.data.recommendations;
    },
    enabled: ({
        isAuthenticated,
        circle,
    }: {
        isAuthenticated: boolean;
        circle?: CircleCircle;
    }) =>
        !!(
            isAuthenticated &&
            circle &&
            circle.recipient.id > 0 &&
            circle.authorizationJWT
        ),
};

export function useCircleRecommendations(circleId: number) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);

    const { data, ...otherProps } = useQuery({
        queryKey: circleRecommendationsQueryOptions.queryKey(circleId),
        queryFn: async () => {
            const hasInitiallyLoadedRecommendations =
                getHasInitiallyLoadedRecommendations();
            const data = await circleRecommendationsQueryOptions.queryFn({
                getAccessTokenSilently,
                circle,
                forceRefresh: !hasInitiallyLoadedRecommendations,
            });

            setHasInitiallyLoadedRecommendations();
            return data ?? {};
        },
        enabled: circleRecommendationsQueryOptions.enabled({
            isAuthenticated,
            circle,
        }),
        staleTime: 1000 * 60 * 15, // 15 minutes
    });

    return {
        ...data,
        ...otherProps,
    };
}

export function useActiveCircleRecommendations() {
    const { activeCircleId } = useActiveCircleId();

    return useCircleRecommendations(activeCircleId);
}

export function useForceRefreshCircleRecommendations() {
    const { getAccessTokenSilently } = useAuth();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (circle?: CircleCircle) => {
            const data = await circleRecommendationsQueryOptions.queryFn({
                getAccessTokenSilently,
                circle,
                forceRefresh: true,
            });
            if (circle) {
                queryClient.setQueryData(
                    circleRecommendationsQueryOptions.queryKey(
                        circle.recipient.id
                    ),
                    data ?? {}
                );
            }
            return data;
        },
    });

    return {
        forceRefreshRecommendations: mutate,
        forceRefreshRecommendationsAsync: mutateAsync,
        ...otherProps,
    };
}
